'use client'

import OverviewDriversTable from '@/components/_championship/tables/overview-drivers/overview-drivers-table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useRouter } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'
import { eventAppliesToSelectedSplits } from '../utils'
import DetailsSection from './details-section'
import EventsSchedule from './events-schedule'
import RulesSection from './rules-section'
import SelectChampionshipSplitsButton from './select-championship-splits-button'
import StandingsSection from './standings/standings-section'

const tabs = [
  { label: 'Details', value: 'Details' },
  { label: 'Event Schedule', value: 'Event-Schedule' },
  { label: 'Rules', value: 'Rules' },
  { label: 'Drivers', value: 'Drivers' },
  { label: 'Results & Standings', value: 'Standings' },
]

export default function TabsSection({
  searchParams,
  championship,
  events,
  championshipAttendees,
  championshipAttendeesLoading,
}) {
  const router = useRouter()
  const activeTab = searchParams.tabName || 'Details'
  const tabsRefs = useRef({})

  const [selectedSplits, setSelectedSplits] = useState(
    championship.splits.map((s) => s.id),
  )

  const handleTabChange = (value) => {
    router.push(
      `/c/${championship.community.slug || championship.communityId}/${championship.slug || championship.id}?tabName=${value}`,
      {
        scroll: false,
      },
    )
  }

  useEffect(() => {
    if (tabsRefs.current[activeTab]) {
      try {
        tabsRefs.current[activeTab].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        })
      } catch (error) {
        // Silently handle scrollIntoView errors (can occur in Chrome with smooth scrolling)
        console.debug('scrollIntoView error:', error)
      }
    }
  }, [activeTab])

  const splitDrivers = useMemo(() => {
    if (!championshipAttendees) return []
    if (championship.splits.length === 0) return championshipAttendees

    return championshipAttendees.filter((attendee) => {
      if (!attendee.splitId) return selectedSplits.length === 0
      return selectedSplits.includes(attendee.splitId)
    })
  }, [championshipAttendees, selectedSplits, championship.splits])

  const splitEvents = useMemo(() => {
    if (!events) return []
    if (championship.splits.length === 0) return events

    return events.filter((event) =>
      eventAppliesToSelectedSplits(event, selectedSplits),
    )
  }, [events, selectedSplits, championship.splits])

  return (
    <div className="mt-5 w-full desktop-xs:mt-10">
      <Tabs
        defaultValue="racing"
        className="w-full"
        onValueChange={(value) => handleTabChange(value)}
        value={activeTab}
      >
        <div className="desktop-xs:screen-grid">
          <div className="desktop-xs:grid-indent">
            <TabsList className="no-scrollbar overflow-x-auto overflow-y-hidden">
              {tabs.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  ref={(el) => (tabsRefs.current[tab.value] = el)}
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </div>

        <div className="screen-grid">
          <div className="grid-indent">
            <TabsContent value="Details">
              <DetailsSection
                championship={championship}
                events={events}
                eventsLoading={false}
              />
            </TabsContent>
            <TabsContent value="Event-Schedule">
              <EventsSchedule
                championship={championship}
                events={splitEvents}
                selectedSplits={selectedSplits}
                setSelectedSplits={setSelectedSplits}
              />
            </TabsContent>
            <TabsContent value="Rules">
              <RulesSection championship={championship} />
            </TabsContent>
            <TabsContent value="Drivers">
              <div className="mt-5 flex flex-col items-start gap-5">
                {championship.splits.length > 0 && (
                  <>
                    <SelectChampionshipSplitsButton
                      selectedSplits={selectedSplits}
                      setSelectedSplits={setSelectedSplits}
                      splits={championship.splits}
                    />

                    <div className="divide"></div>
                  </>
                )}

                <OverviewDriversTable
                  attendees={splitDrivers}
                  isLoading={championshipAttendeesLoading}
                  isSingleEvent={championship.isSingleEvent}
                  isManagerSide={false}
                />
              </div>
            </TabsContent>
            <TabsContent value="Standings">
              <StandingsSection
                championship={championship}
                events={events}
                selectedSplits={selectedSplits}
                setSelectedSplits={setSelectedSplits}
              />
            </TabsContent>
          </div>
        </div>
      </Tabs>
    </div>
  )
}
